import React, {useContext} from "react";
import PropTypes from "prop-types";
import {DateRangePicker as ReactDateRangePicker} from "react-dates";

import {Box, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {DateRangePickerContext} from "../contexts/DateRangePickerContext";
import {UserAgentUtils} from "../utils/useragent";
import {black500, greyishBlue100, greyishBlue200, greyishBlue400, greyishBlue600, greyishBlue900} from "../assets/jss/main";

const DEFAULT_DISPLAY_FORMAT = 'D MMMM YYYY';
const COMPACT_PLACEHOLDER = '--/--/----';

function MobileDateRangePicker({ startDate, endDate, focusedInput, setFocusedInput, setStartDate, setEndDate, handleDateChange, clearDates, displayFormat }) {
    const isDefaultFormat = displayFormat === DEFAULT_DISPLAY_FORMAT;
    const sameYear = startDate && endDate && startDate.year() === endDate.year();
    const startFormat = isDefaultFormat && sameYear ? 'D MMMM' : displayFormat;

    const formattedStart = startDate ? startDate.format(startFormat) : '-';
    const formattedEnd = endDate ? endDate.format(displayFormat) : '-';

    function handleClear(e) {
        e.stopPropagation();
        clearDates();
    }

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
                onClick={() => setFocusedInput('startDate')}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    border: `1px solid ${greyishBlue100}`,
                    borderRadius: '8px',
                    padding: '9px 12px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    userSelect: 'none',
                    minWidth: 0,
                    width: '100%'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={greyishBlue600} strokeWidth="2" style={{ flexShrink: 0 }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <Box component="span" sx={{ fontSize: '13px', color: greyishBlue900, whiteSpace: 'nowrap' }}>
                        {formattedStart}
                        <Box component="span" sx={{ mx: '6px', color: greyishBlue600, display: 'inline-flex', verticalAlign: 'middle' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        </Box>
                        {formattedEnd}
                    </Box>
                </Box>
                {(startDate || endDate) && (
                    <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{
                            p: '2px',
                            color: greyishBlue400,
                            flexShrink: 0,
                            background: greyishBlue100
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}>
                <ReactDateRangePicker
                    startDateId="startDate-mobile"
                    endDateId="endDate-mobile"
                    startDate={startDate}
                    endDate={endDate}
                    onDatesChange={({ startDate, endDate }) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                    }}
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                    onClose={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
                    displayFormat={displayFormat}
                    readOnly={true}
                    isOutsideRange={() => false}
                    withFullScreenPortal={true}
                    orientation="vertical"
                    numberOfMonths={2}
                    minimumNights={0}
                    hideKeyboardShortcutsPanel={true}
                    showClearDates={true}
                    reopenPickerOnClearDates={true}
                    showDefaultInputIcon={true}
                />
            </Box>
        </Box>
    );
}

function CompactDateRangePicker({ startDate, endDate, focusedInput, setFocusedInput, setStartDate, setEndDate, handleDateChange, clearDates, displayFormat, suspended = false }) {
    const formattedStart = !suspended && startDate ? startDate.format(displayFormat) : COMPACT_PLACEHOLDER;
    const formattedEnd = !suspended && endDate ? endDate.format(displayFormat) : COMPACT_PLACEHOLDER;

    function handleClear(e) {
        e.stopPropagation();
        clearDates();
    }

    return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
                onClick={() => setFocusedInput('startDate')}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '40px',
                    padding: '12px 16px',
                    border: `1px solid ${greyishBlue200}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    userSelect: 'none'
                }}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M5.33333 1.33333V3.33333" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.6667 1.33333V3.33333" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.33333 6.06H13.6667" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 5.66667V11.3333C14 13.3333 13 14.6667 10.6667 14.6667H5.33333C3 14.6667 2 13.3333 2 11.3333V5.66667C2 3.66667 3 2.33333 5.33333 2.33333H10.6667C13 2.33333 14 3.66667 14 5.66667Z" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.4631 9.13334H10.4691" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.4631 11.1333H10.4691" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.99699 9.13334H8.00298" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7.99699 11.1333H8.00298" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.52954 9.13334H5.53553" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.52954 11.1333H5.53553" stroke={black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <Box component="span" sx={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', color: !suspended && startDate ? black500 : greyishBlue400 }}>
                    {formattedStart}
                </Box>

                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M14.43 5.93L20.5 12L14.43 18.07" stroke={suspended ? greyishBlue400 : black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3.5 12H20.33" stroke={suspended ? greyishBlue400 : black500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <Box component="span" sx={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', color: !suspended && endDate ? black500 : greyishBlue400 }}>
                    {formattedEnd}
                </Box>

                {!suspended && (startDate || endDate) && (
                    <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{ p: '2px', flexShrink: 0 }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3.9608 3.96073L12.0001 12" stroke={greyishBlue400} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3.96087 12L12.0001 3.96073" stroke={greyishBlue400} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </IconButton>
                )}
            </Box>

            <Box sx={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}>
                <ReactDateRangePicker
                    startDateId="startDate-compact"
                    endDateId="endDate-compact"
                    startDate={startDate}
                    endDate={endDate}
                    onDatesChange={({ startDate, endDate }) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                    }}
                    focusedInput={focusedInput}
                    onFocusChange={setFocusedInput}
                    onClose={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
                    displayFormat={displayFormat}
                    readOnly={true}
                    isOutsideRange={() => false}
                    withFullScreenPortal={UserAgentUtils.isMobile()}
                    // react-dates always portals to the body when withFullScreenPortal
                    // is on, so appendToBody isn't needed there - and it must stay off
                    // in that case, since react-dates' own fullscreen close button is
                    // rendered inside the same container appendToBody checks against,
                    // making onOutsideClick bail out and the close button do nothing.
                    // disableScroll keeps the body-scroll-lock that appendToBody would
                    // otherwise have provided.
                    appendToBody={!UserAgentUtils.isMobile()}
                    disableScroll={UserAgentUtils.isMobile()}
                    orientation={UserAgentUtils.isMobile() ? "vertical" : "horizontal"}
                    numberOfMonths={2}
                    minimumNights={0}
                    hideKeyboardShortcutsPanel={true}
                    showClearDates={true}
                    reopenPickerOnClearDates={true}
                    showDefaultInputIcon={true}
                    anchorDirection={"right"}
                />
            </Box>
        </Box>
    );
}

DateRangePicker.propTypes = {
    displayFormat: PropTypes.string,
    compact: PropTypes.bool,
    // shows the picker as inert (placeholders, no clear button) without actually
    // touching startDate/endDate - for callers where the period is temporarily
    // ignored (e.g. a search overriding it) but shouldn't be lost from state
    suspended: PropTypes.bool
};

export default function DateRangePicker({ displayFormat = DEFAULT_DISPLAY_FORMAT, compact = false, suspended = false }) {
    const {
        startDate,
        endDate,
        handleDateChange,
        clearDates,
        focusedInput,
        setStartDate,
        setEndDate,
        setFocusedInput,
    } = useContext(DateRangePickerContext);

    if (compact) {
        return (
            <CompactDateRangePicker
                startDate={startDate}
                endDate={endDate}
                focusedInput={focusedInput}
                setFocusedInput={setFocusedInput}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateChange={handleDateChange}
                clearDates={clearDates}
                displayFormat={displayFormat}
                suspended={suspended}
            />
        );
    }

    if (UserAgentUtils.isMobile()) {
        return (
            <MobileDateRangePicker
                startDate={startDate}
                endDate={endDate}
                focusedInput={focusedInput}
                setFocusedInput={setFocusedInput}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateChange={handleDateChange}
                clearDates={clearDates}
                displayFormat={displayFormat}
            />
        );
    }

    return (
        <ReactDateRangePicker
            startDateId="startDate"
            endDateId="endDate"
            startDatePlaceholderText={"-"}
            endDatePlaceholderText={"-"}
            startDate={startDate}
            endDate={endDate}
            onDatesChange={({startDate, endDate}) => {
                setStartDate(startDate);
                setEndDate(endDate);
            }}
            focusedInput={focusedInput}
            onFocusChange={(focusedInput) => {
               setFocusedInput(focusedInput)
            }}
            onClose={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
            displayFormat={displayFormat}
            block={true}
            readOnly={true}
            isOutsideRange={() => false}
            showDefaultInputIcon={true}
            showClearDates={true}
            reopenPickerOnClearDates={true}
            required={true}
            hideKeyboardShortcutsPanel={true}
            withFullScreenPortal={window.innerWidth < 768}
            numberOfMonths={2}
            orientation={window.innerWidth < 768 ? "vertical" : "horizontal"}
            anchorDirection={"right"}
            minimumNights={0}
        />
    )
}
